import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, UserPlus, Trash2, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminManagement() {
  const { session } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('standard');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/admin/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
        
        // Find current admin
        const userEmail = authSession?.user?.email;
        const current = data.find(a => a.email === userEmail);
        setCurrentAdmin(current);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Admin listesi yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newEmail.trim()) {
      toast.error('E-posta adresi gerekli');
      return;
    }

    setSaving(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/admin/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: newEmail, role: newRole })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eklenemedi');
      }

      toast.success('Admin eklendi');
      setAddDialogOpen(false);
      setNewEmail('');
      setNewRole('standard');
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = async (adminId, adminEmail) => {
    if (!confirm(`${adminEmail} adresli admini silmek istediginizden emin misiniz?`)) return;

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/admin/admins/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Silinemedi');
      }

      toast.success('Admin silindi');
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const isGuardian = currentAdmin?.role === 'guardian';

  return (
    <div data-testid="admin-management">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-brand text-2xl text-brand-ink">Admin Yonetimi</h2>
        {isGuardian && (
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-admin-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Admin Ekle
          </Button>
        )}
      </div>

      {!isGuardian && (
        <Card className="mb-4 bg-brand-red-soft border-brand-red">
          <CardContent className="py-3">
            <p className="text-sm text-brand-red-deep">
              Admin ekleme/silme islemi sadece Guardian yetkisine sahip adminler tarafindan yapilabilir.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Adminler ({admins.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-500 py-4">Yukleniyor...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Eklenme Tarihi</TableHead>
                  {isGuardian && <TableHead className="text-right">Islem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">{admin.email}</TableCell>
                    <TableCell>
                      <Badge variant={admin.role === 'guardian' ? 'default' : 'secondary'} className={admin.role === 'guardian' ? 'bg-brand-ink text-white border-brand-ink' : ''}>
                        {admin.role === 'guardian' ? (
                          <><Crown className="w-3 h-3 mr-1" /> Guardian</>
                        ) : 'Standart'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={admin.enabled ? 'default' : 'destructive'} className={admin.enabled ? 'bg-green-100 text-green-800' : ''}>
                        {admin.enabled ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(admin.created_at).toLocaleDateString('tr-TR')}
                    </TableCell>
                    {isGuardian && (
                      <TableCell className="text-right">
                        {admin.id !== currentAdmin?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                            data-testid={`remove-admin-${admin.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Admin Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">E-posta Adresi</label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                className="mt-1"
                data-testid="new-admin-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Rol</label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="mt-1" data-testid="new-admin-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standart (Admin silme/ekleme haric tum yetkiler)</SelectItem>
                  <SelectItem value="guardian">Guardian (Tam yetki)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Iptal</Button>
            <Button onClick={handleAddAdmin} disabled={saving} data-testid="confirm-add-admin">
              {saving ? 'Ekleniyor...' : 'Ekle'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
